import { visionLabApi } from '@/app/apiClient';
import type {
  LibraryCreateInputMap,
  LibrarySnapshotMap,
} from '@/features/metadata-library/types/libraryRequests';
import type {
  CollectionSlug,
  LibraryFieldsCatalog,
  LibraryRecordMap,
  LibraryRecordsPage,
} from '@/features/metadata-library/types/metadataLibrary';
import type { ApiCallOptions } from '@/shared/api/client';
import type { SupportedLocale } from '@/shared/types/i18n';

export function fetchLibraryRecords<TCollection extends CollectionSlug>(
  collection: TCollection,
  params: {
    q?: string;
    cursor?: string;
    limit: number;
    sort?: 'alpha';
    order?: 'asc';
    organizationId?: readonly string[];
  },
  options: ApiCallOptions = {},
) {
  return visionLabApi.get<LibraryRecordsPage<LibraryRecordMap[TCollection]>>(`/${collection}`, {
    ...options,
    searchParams: {
      q: params.q?.trim() || undefined,
      sort: params.sort,
      order: params.order,
      organizationId: params.organizationId,
      cursor: params.cursor || undefined,
      limit: params.limit,
    },
  });
}

export function fetchLibraryFields(
  collection: CollectionSlug,
  locale: SupportedLocale,
  options: ApiCallOptions = {},
) {
  return visionLabApi.get<LibraryFieldsCatalog>(`/${collection}/fields`, {
    ...options,
    searchParams: { locale },
  });
}

export function fetchLibraryRecord<TCollection extends CollectionSlug>(
  collection: TCollection,
  recordId: string,
  options: ApiCallOptions = {},
) {
  return visionLabApi.get<LibraryRecordMap[TCollection]>(
    `/${collection}/${encodeURIComponent(recordId)}`,
    options,
  );
}

export function createLibraryRecord<TCollection extends CollectionSlug>(
  collection: TCollection,
  body: LibraryCreateInputMap[TCollection],
) {
  return visionLabApi.post<LibraryRecordMap[TCollection]>(`/${collection}`, { json: body });
}

export function patchLibraryRecord<TCollection extends CollectionSlug>(
  collection: TCollection,
  recordId: string,
  body: LibrarySnapshotMap[TCollection],
) {
  return visionLabApi.patch<LibraryRecordMap[TCollection]>(
    `/${collection}/${encodeURIComponent(recordId)}`,
    { json: body },
  );
}

export function deleteLibraryRecord(collection: CollectionSlug, recordId: string) {
  return visionLabApi.delete<void>(`/${collection}/${encodeURIComponent(recordId)}`, {
    responseType: 'void',
  });
}
